import React, { useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import useComponentSize from "@rehooks/component-size";
import at from "lodash/at";
import { Button, TextField, NumberField, Tooltip } from "react95";
import { useDispatch, useSelector } from "react-redux";
import ReactGA from "react-ga";
import useSelectedScraper from "../../hooks/useSelectedScraper";
import { searchForArtist } from "../../reducers/artistSearch";
import { updateArtistName, clearArtistName } from "../../reducers/artistName";
import { updateTrackName, clearTrackName } from "../../reducers/trackName";
import { updateMaxResults } from "../../reducers/maxResults";
import { clearSearchFilters } from "../../reducers/searchFilters";

const Wrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: flex-end;
    flex-flow: row wrap;
    align-items: center;
    margin-bottom: 5px;

    @media (max-width: 768px) {
        justify-content: space-between;
    }
`;

const FieldWrapper = ({ label, children }) => {
    return (
        <Wrapper>
            {label}:&nbsp;
            {children}
        </Wrapper>
    );
};

FieldWrapper.propTypes = {
    children: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired
};

const Container = styled.div`
    width: 45%;
    margin-right: 10px;

    @media (max-width: 768px) {
        margin-right: 0;
        margin-bottom: 15px;
        width: 100%;
    }
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
    margin-top: 15px;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StandardSearch = () => {
    const dispatch = useDispatch();
    const selectedScraper = useSelectedScraper();
    const [artistName = "", trackName = "", maxResults = 0] = useSelector((state) =>
        at(
            state,
            `artistName.${selectedScraper}.value`,
            `trackName.${selectedScraper}.value`,
            `maxResults.${selectedScraper}.value`
        )
    );

    const ref = useRef(null);
    const windowSize = useComponentSize(ref);

    let fieldWidth = "72%";
    if (windowSize.width <= 300) {
        fieldWidth = "100%";
    }

    return (
        <Container ref={ref}>
            <Fields>
                <Tooltip text="Enter the artist's name from the URL" delay={0}>
                    <FieldWrapper label="Artist">
                        <TextField
                            value={artistName}
                            onChange={(e) =>
                                dispatch(updateArtistName(e.target.value.replace(/ /g, "")))
                            }
                            shadow={false}
                            width={fieldWidth}
                        />
                    </FieldWrapper>
                </Tooltip>
                <FieldWrapper label="Track Title">
                    <TextField
                        name="title"
                        value={trackName}
                        onChange={(e) => dispatch(updateTrackName(e.target.value))}
                        shadow={false}
                        width={fieldWidth}
                    />
                </FieldWrapper>
                <FieldWrapper label="Max Results">
                    <NumberField
                        width={fieldWidth}
                        shadow={false}
                        value={maxResults}
                        onChange={(value) => dispatch(updateMaxResults(value))}
                    />
                </FieldWrapper>
            </Fields>

            <ButtonContainer>
                <Button
                    style={{ flex: "1 1 0", width: "0", marginRight: "4px" }}
                    onClick={() => {
                        dispatch(clearSearchFilters());
                        dispatch(clearArtistName());
                        dispatch(clearTrackName());
                    }}
                >
                    Clear Fields
                </Button>
                <Button
                    onClick={() => {
                        dispatch(searchForArtist(artistName));
                        ReactGA.event({
                            category: "User",
                            action: `Searched for ${artistName}`
                        });
                    }}
                    style={{ flex: "1 1 0", width: "0" }}
                    disabled={!artistName}
                >
                    Find it!
                </Button>
            </ButtonContainer>
        </Container>
    );
};

export default StandardSearch;
